import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLoder from '../../components/PageLoader';
import exportEvaluationToPDF, { loadImageAsDataUrl, resolveFileUrl } from '../../components/ExportEvaluationPDF';
import "../../styles/ConsultantEvaluation.css";
import './compact-eval.css';

export default function EditAdminEvaluation() {
     const { id } = useParams();
     const navigate = useNavigate();
     const instanceId = React.useRef(Math.random().toString(36).slice(2, 9));
     useEffect(() => {
          const iid = instanceId.current;
          console.debug('[EditAdminEvaluation] mount', iid, { id });
          return () => console.debug('[EditAdminEvaluation] unmount', iid, { id });
     }, [id]);
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const [loading, setLoading] = useState(true);
     const [saving, setSaving] = useState(false);
     const [questions, setQuestions] = useState([]);
     // helper to map points to pastel background colors
     const getBgColor = (points) => {
          switch (String(points)) {
               case '3':
                    return '#e6ffed'; // pastel green
               case '2':
                    return '#fff8e6'; // pastel orange
               case '1':
                    return '#ffecec'; // pastel red
               case '0':
                    return '#f3f3f3'; // light gray
               default:
                    return '';
          }
     };

     const getTextColor = (points) => {
          switch (String(points)) {
               case '3':
                    return '#1f7a1f';
               case '2':
                    return '#a05a00';
               case '1':
                    return '#b30000';
               case '0':
                    return '#666666';
               default:
                    return 'inherit';
          }
     };
     const computeVdc = (dojStr) => {
          if (!dojStr) return '';
          let ds = String(dojStr).trim();
          let d = null;
          if (/^\d{4}-\d{2}-\d{2}/.test(ds)) {
               d = new Date(ds.split(' ')[0]);
          } else if (/^\d{2}-\d{2}-\d{4}$/.test(ds)) {
               const [dd, mm, yyyy] = ds.split('-');
               d = new Date(`${yyyy}-${mm}-${dd}`);
          } else {
               const tmp = new Date(ds);
               if (!isNaN(tmp.getTime())) d = tmp;
          }
          if (!d || isNaN(d.getTime())) return '';
          const now = new Date();
          const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
          if (months < 0) return '';
          const yrs = Math.floor(months / 12);
          const rem = months % 12;
          const parts = [];
          if (yrs > 0) parts.push(`${yrs} year${yrs > 1 ? 's' : ''}`);
          if (rem > 0) parts.push(`${rem} month${rem > 1 ? 's' : ''}`);
          return parts.length > 0 ? parts.join(' ') : '0 months';
     };
     const formatDateTime = (s) => {
          if (!s) return '';
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const hh = String(d.getHours()).padStart(2, '0');
                    const min = String(d.getMinutes()).padStart(2, '0');
                    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
               }
          } catch (e) {
               // ignore
          }
          return s;
     };
     const formatDateOnly = (s) => {
          if (!s) return '';
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    return `${dd}-${mm}-${yyyy}`;
               }
          } catch (e) { }
          return String(s).split('T')[0] || String(s);
     };
     const [form, setForm] = useState({ emp_code: '', emp_name: '', quarter: '', centre: '', created_by: '', specialty: '', timing: '', year: '', mobile: '', employment_type: '', doj: '', vdc_exp: '', dept: '', is_submitted: 'N' });
     const [details, setDetails] = useState({});
     const [signInfo, setSignInfo] = useState(null);
     const [adminSignInfo, setAdminSignInfo] = useState(null);
     const [message, setMessage] = useState(null);
     const [detailErrors, setDetailErrors] = useState({});
     const userRole = (sessionStorage.getItem('user_role') || localStorage.getItem('userRole') || '').toString().toUpperCase();
     const isAdminUser = userRole.includes('ADMIN') || userRole.includes('HR') || userRole === 'SUPER_ADMIN' || userRole.includes('LAB_DIRECTOR') || userRole === 'LAB_DIRECTOR';

     useEffect(() => {
          let mounted = true;

          const load = async () => {
               setLoading(true);
               try {
                    const token = localStorage.getItem('token');
                    const headers = { Accept: 'application/json' };
                    if (token) headers.Authorization = `Bearer ${token}`;

                    // fetch evaluation
                    const evalUrl = `${baseUrl}/getAdminEvaluation/${id}`;
                    const res = await fetch(evalUrl, { method: 'GET', headers });
                    const json = await res.json();

                    let ev = null;
                    // build a serverDetailsMap to use when merging with questions
                    let serverDetailsMap = {};
                    if (json && json.evaluation) {
                         ev = json.evaluation;
                         const arr = Array.isArray(json.details) ? json.details : [];
                         serverDetailsMap = arr.reduce((acc, d) => ({
                              ...acc,
                              [d.question_id ?? d.id]: {
                                   question_id: d.question_id ?? d.id,
                                   points: d.points,
                                   comments: d.comments ?? '',
                                   admin_points: (d.admin_points === 0 ? '' : (d.admin_points ?? d.adminPoints ?? '')),
                                   admin_comments: d.admin_comments ?? d.adminComments ?? ''
                              }
                         }), {});
                    } else if (Array.isArray(json) && json.length) {
                         ev = json[0];
                         const arr = Array.isArray(json[0].details) ? json[0].details : [];
                         serverDetailsMap = arr.reduce((acc, d) => ({ ...acc, [d.question_id ?? d.id]: { question_id: d.question_id ?? d.id, points: d.points, comments: d.comments ?? '', admin_points: (d.admin_points === 0 ? '' : (d.admin_points ?? d.adminPoints ?? '')), admin_comments: d.admin_comments ?? d.adminComments ?? '' } }), {});
                    } else if (json && typeof json === 'object') {
                         ev = json;
                         const arr = Array.isArray(json.details) ? json.details : [];
                         serverDetailsMap = arr.reduce((acc, d) => ({
                              ...acc,
                              [d.question_id ?? d.id]: {
                                   question_id: d.question_id ?? d.id,
                                   points: d.points,
                                   comments: d.comments ?? '',
                                   admin_points: (d.admin_points === 0 ? '' : (d.admin_points ?? d.adminPoints ?? '')),
                                   admin_comments: d.admin_comments ?? d.adminComments ?? ''
                              }
                         }), {});
                    }

                    // set details map from server first
                    setDetails(serverDetailsMap);

                    if (ev) {
                         const pick = (obj, keys, fallback = '') => {
                              for (const k of keys) if (obj && (obj[k] !== undefined && obj[k] !== null)) return obj[k];
                              return fallback;
                         };

                         const computed = {
                              emp_code: pick(ev, ['emp_code'], ''),
                              emp_name: pick(ev, ['emp_name', 'employee_name', 'comp_name',], ''),
                              quarter: pick(ev, ['quarter'], ''),
                              centre: pick(ev, ['centre', 'location_name', 'centre_name', 'location'], ''),
                              created_by: pick(ev, ['created_by',], ''),
                              specialty: pick(ev, ['specialty', 'speciality'], ''),
                              timing: pick(ev, ['timing', 'shift'], ''),
                              year: pick(ev, ['year'], ''),
                              mobile: pick(ev, ['mobile', 'mobile_no', 'mobile_number', 'phone', 'contact_number', 'contact'], ''),
                              employment_type: pick(ev, ['employment_type', 'employment', 'emp_type'], ''),
                              doj: pick(ev, ['doj', 'date_of_joining',], ''),
                              vdc_exp: pick(ev, ['vdc_exp',], ''),
                              overall_comment: pick(ev, ['overall_comment',], ''),
                              dept: pick(ev, ['dept',], ''),
                              is_submitted: pick(ev, ['is_submitted',], 'N'),
                              submitted_on: pick(ev, ['updated_at', 'created_at'], ''),
                              created_on: pick(ev, ['created_dtm',], ''),
                              updated_on: pick(ev, ['updated_dtm',], ''),
                              total_admin_points: pick(ev, ['total_admin_points'], ''),
                         };

                         // compute initial vdc_exp if doj is present; prefer vdc from server if available
                         const initialVdc = computeVdc(computed.doj);
                         setForm({ ...computed, vdc_exp: computed.vdc_exp || initialVdc });

                         // capture sign info from server response when present
                         setSignInfo(ev.sign || null);
                         setAdminSignInfo(ev.admin_sign || null);

                         // If key profile fields are missing from the evaluation, try to fetch employee details
                         const dojMissing = (!computed.doj || computed.doj === '' || computed.doj === '0000-00-00' || String(computed.doj).startsWith('0000'));
                         const needs = (!computed.mobile || computed.mobile === '') || (!computed.dept || computed.dept === '') || dojMissing || (!computed.employment_type || computed.employment_type === '');
                         if (needs && computed.emp_code) {
                              try {
                                   const eUrl = `${baseUrl}/getEmployeeDetails/${encodeURIComponent(computed.emp_code)}`;
                                   const eHeaders = { Accept: 'application/json' };
                                   const token2 = localStorage.getItem('token');
                                   if (token2) eHeaders.Authorization = `Bearer ${token2}`;
                                   const eres = await fetch(eUrl, { method: 'GET', headers: eHeaders });
                                   const edata = await eres.json();
                                   if (Array.isArray(edata) && edata.length > 0) {
                                        const emp = edata[0];
                                        setForm(f => {
                                             const resolvedDoj = (f.doj && f.doj !== '0000-00-00') ? f.doj : (emp.doj || emp.date_of_joining || emp.join_date || '');
                                             const resolvedMobile = f.mobile || emp.mobile || emp.mobile_no || emp.contact || emp.phone || '';
                                             const resolvedDept = f.dept || emp.dept_name || emp.department || emp.dept || '';
                                             const resolvedEmployment = f.employment_type || emp.employment_type || emp.emp_type || emp.employment || '';
                                             return ({
                                                  ...f,
                                                  mobile: resolvedMobile,
                                                  doj: resolvedDoj,
                                                  dept: resolvedDept,
                                                  employment_type: resolvedEmployment,
                                                  vdc_exp: computeVdc(resolvedDoj) || f.vdc_exp
                                             });
                                        });
                                   }
                              } catch (ex) {
                                   console.debug('Failed to fetch employee details', ex);
                              }
                         }
                    }

                    // fetch questions list (to render selects in correct order)
                    const qUrl = `${baseUrl}/getConsultantEvaluationQuestions`;
                    const qres = await fetch(qUrl, { method: 'GET', headers });
                    const qjson = await qres.json();
                    if (Array.isArray(qjson)) {
                         setQuestions(qjson);
                         // ensure details has entries for all questions using serverDetailsMap
                         const init = (qjson || []).reduce((acc, q) => {
                              const key = q.id;
                              const existing = serverDetailsMap[key] || {};
                              acc[key] = {
                                   question_id: key,
                                   points: existing.points === undefined ? '' : existing.points,
                                   comments: existing.comments || '',
                                   admin_points: existing.admin_points === undefined ? '' : existing.admin_points,
                                   admin_comments: existing.admin_comments || ''
                              };
                              return acc;
                         }, {});
                         setDetails(init);
                    }
               } catch (err) {
                    console.error(err);
                    setMessage({ type: 'error', text: 'Failed to load evaluation' });
               } finally {
                    if (mounted) setLoading(false);
               }
          };

          load();
          return () => { mounted = false; };
     }, [baseUrl, id]);

     // auto-hide toast messages after 3s
     useEffect(() => {
          if (!message) return;
          const t = setTimeout(() => setMessage(null), 3000);
          return () => clearTimeout(t);
     }, [message]);

     function handleDetailChange(questionId, field, value) {
          setDetails(d => ({ ...d, [questionId]: { ...d[questionId], [field]: value } }));
     }

     async function handleSubmit(e, finalSubmit = false) {
          if (e && e.preventDefault) e.preventDefault();
          setMessage(null);
          setSaving(true);
          // enforce admin-only edits when evaluation is submitted
          if (!(isAdminUser && form.is_submitted === 'Y')) {
               setMessage({ type: 'error', text: 'You are not allowed to modify admin fields for this evaluation.' });
               setSaving(false);
               return;
          }

          // validate: when admin points is Non Satisfactory (1) or Nothing is done (0), admin_comments are mandatory
          const errors = {};
          Object.entries(details || {}).forEach(([qid, it]) => {
               const ap = it && (it.admin_points === '' || it.admin_points == null) ? '' : it.admin_points;
               if (ap !== '') {
                    const n = Number(ap);
                    if (n === 1 || n === 0) {
                         const ac = (it.admin_comments || '').toString().trim();
                         if (!ac) errors[qid] = true;
                    }
               }
          });
          if (Object.keys(errors).length > 0) {
               setDetailErrors(errors);
               setMessage({ type: 'error', text: 'Please fill all mandatory fields.' });
               setSaving(false);
               return;
          } else {
               setDetailErrors({});
          }

          // proceed to save without interactive confirmation
          try {
               const token = localStorage.getItem('token');
               const headers = { 'Content-Type': 'application/json' };
               if (token) headers.Authorization = `Bearer ${token}`;

               const payloadForm = { ...form };
               // Prefetch signature images (self and admin) so we can include them in the submission
               const fetchSignatureDataUrlFor = async (code) => {
                    // returns { dataUrl, rawUrl } or null
                    if (!code) return null;
                    // Prefer sign info returned with the evaluation when available
                    try {
                         if (signInfo && String(signInfo.emp_code) === String(code) && signInfo.file_url) {
                              const finalUrl = (/^https?:\/\//i.test(String(signInfo.file_url))) ? signInfo.file_url : resolveFileUrl(signInfo.file_url, baseUrl, process.env.REACT_APP_FILE_URL || '');
                              const data = await loadImageAsDataUrl(finalUrl);
                              return { dataUrl: data, rawUrl: finalUrl };
                         }
                         if (adminSignInfo && String(adminSignInfo.emp_code) === String(code) && adminSignInfo.file_url) {
                              const finalUrl = (/^https?:\/\//i.test(String(adminSignInfo.file_url))) ? adminSignInfo.file_url : resolveFileUrl(adminSignInfo.file_url, baseUrl, process.env.REACT_APP_FILE_URL || '');
                              const data = await loadImageAsDataUrl(finalUrl);
                              return { dataUrl: data, rawUrl: finalUrl };
                         }
                    } catch (e) {
                         // ignore and fall back to candidate endpoints
                    }
                    try {
                         const b = baseUrl ? String(baseUrl).replace(/\/$/, '') : '';
                         const candidates = [];
                         if (b) {
                              candidates.push(`${b}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                              candidates.push(`${b}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         }
                         const envRoot = (process.env.REACT_APP_FILE_URL || '').replace(/\/$/, '');
                         if (envRoot) {
                              candidates.push(`${envRoot}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                              candidates.push(`${envRoot}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         }
                         if (typeof window !== 'undefined' && window.location) {
                              const hostRoot = `${window.location.protocol}//${window.location.host}`;
                              candidates.push(`${hostRoot}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                              candidates.push(`${hostRoot}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         }
                         const uniq = Array.from(new Set(candidates.filter(Boolean)));
                         const headersReq = { Accept: 'application/json' };
                         const token = localStorage.getItem('token');
                         if (token) headersReq.Authorization = `Bearer ${token}`;
                         for (const u of uniq) {
                              try {
                                   const r = await fetch(u, { method: 'GET', headers: headersReq });
                                   if (!r.ok) continue;
                                   const j = await r.json();
                                   const arr = Array.isArray(j) ? j : (j && Array.isArray(j.data) ? j.data : []);
                                   if (!arr || !arr.length) continue;
                                   let match = arr.find(s => String(s.emp_code) === String(code) || String(s.emp_code) === String(code).replace(/^0+/, ''));
                                   if (!match) match = arr.find(s => String(s.file_url || s.file || s.url || '').includes(String(code)));
                                   if (!match) continue;
                                   const raw = match.file_url || match.file || match.url || null;
                                   if (!raw) continue;
                                   // Derive absolute URL: API often returns absolute base_url('uploads/signs/...') already
                                   const finalUrl = (/^https?:\/\//i.test(String(raw))) ? raw : resolveFileUrl(raw, baseUrl, process.env.REACT_APP_FILE_URL || '');
                                   // Try to convert to data URL; if CORS prevents it we'll still return raw URL
                                   const data = await loadImageAsDataUrl(finalUrl);
                                   return { dataUrl: data, rawUrl: finalUrl };
                              } catch (e) {
                                   // try next
                              }
                         }
                    } catch (e) {
                         // ignore
                    }
                    return null;
               };

               // Prefetch both signatures (self and admin) before sending payload
               let selfSignData = null;
               let adminSignData = null;
               try {
                    // Prefer sign objects returned with the evaluation when present
                    if (signInfo && signInfo.file_url) {
                         try {
                              const final = (/^https?:\/\//i.test(String(signInfo.file_url))) ? signInfo.file_url : resolveFileUrl(signInfo.file_url, baseUrl, process.env.REACT_APP_FILE_URL || '');
                              const data = await loadImageAsDataUrl(final);
                              selfSignData = { dataUrl: data, rawUrl: final };
                         } catch (e) { /* ignore */ }
                    }
                    if (adminSignInfo && adminSignInfo.file_url) {
                         try {
                              const finalA = (/^https?:\/\//i.test(String(adminSignInfo.file_url))) ? adminSignInfo.file_url : resolveFileUrl(adminSignInfo.file_url, baseUrl, process.env.REACT_APP_FILE_URL || '');
                              const dataA = await loadImageAsDataUrl(finalA);
                              adminSignData = { dataUrl: dataA, rawUrl: finalA };
                         } catch (e) { /* ignore */ }
                    }

                    // If either signature is still missing, fetch via consultantSigns endpoints
                    if (!selfSignData || !adminSignData) {
                         const selfCode = payloadForm.emp_code || form.emp_code || '';
                         const adminCodeForFetch = adminEmp || '';
                         const results = await Promise.all([fetchSignatureDataUrlFor(selfCode), fetchSignatureDataUrlFor(adminCodeForFetch)]);
                         selfSignData = selfSignData || results[0];
                         adminSignData = adminSignData || results[1];
                    }
               } catch (e) {
                    // proceed without sign images
               }
               if (finalSubmit) payloadForm.is_submitted = 'Y';

               // try to infer current admin user emp code and name from common storage keys
               const getFromStorage = (keys) => {
                    for (const k of keys) {
                         const v = sessionStorage.getItem(k) || localStorage.getItem(k);
                         if (v) return v;
                    }
                    return null;
               };

               // Primary source used across the app: sessionStorage.user_code / user_name
               let adminEmp = sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || null;
               let adminName = sessionStorage.getItem('user_name') || localStorage.getItem('user_name') || null;

               // If not available, try parsed userProfile which some components store
               if ((!adminEmp || !adminName)) {
                    const upRaw = sessionStorage.getItem('userProfile') || localStorage.getItem('userProfile');
                    if (upRaw) {
                         try {
                              const up = JSON.parse(upRaw);
                              adminEmp = adminEmp || up.user_code || up.employee_code || up.emp_code || up.empCode || null;
                              adminName = adminName || up.user_name || up.employee_name || up.full_name || up.name || null;
                         } catch (e) {
                              // ignore parse errors
                         }
                    }
               }

               // final fallbacks to other possible keys
               adminEmp = adminEmp || getFromStorage(['emp_code', 'empCode', 'user_emp_code', 'userId', 'user_id', 'empId', 'emp']);
               adminName = adminName || getFromStorage(['emp_name', 'empName', 'user_name', 'userName', 'name', 'fullName']);

               const payload = { ...payloadForm, details: Object.values(details), admin_emp_code: adminEmp, admin_name: adminName, self_signature: selfSignData, admin_signature: adminSignData };
               // compute total_points and total_admin_points
               payload.total_points = Object.values(details).reduce((s, it) => s + (parseInt(it.points, 10) || 0), 0);
               payload.total_admin_points = Object.values(details).reduce((s, it) => s + (parseInt(it.admin_points, 10) || 0), 0);

               const url = `${baseUrl}/updateConsultantEvaluation/${id}`;
               const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
               const json = await res.json();
               if (res.ok) {
                    setMessage({ type: 'success', text: 'Saved' });
                    // navigate to admin view after save (both draft and final)
                    if (finalSubmit) {
                         setForm(f => ({ ...f, is_submitted: 'Y', submitted_on: new Date().toISOString() }));
                    }
                    // After successful save, generate PDF: open in new tab and download
                    try {
                         // Build prefetched arrays preferring `signInfo` / `adminSignInfo` from evaluation
                         let exportPrefetchedSigDataUrls = [selfSignData ? selfSignData.dataUrl : null, adminSignData ? adminSignData.dataUrl : null];
                         let exportPrefetchedRawSigUrls = [selfSignData ? selfSignData.rawUrl : null, adminSignData ? adminSignData.rawUrl : null];
                         try {
                              if (signInfo && signInfo.file_url) {
                                   const final = (/^https?:\/\//i.test(String(signInfo.file_url))) ? signInfo.file_url : resolveFileUrl(signInfo.file_url, baseUrl, process.env.REACT_APP_FILE_URL || '');
                                   exportPrefetchedRawSigUrls[0] = final;
                                   exportPrefetchedSigDataUrls[0] = await loadImageAsDataUrl(final);
                              }
                              if (adminSignInfo && adminSignInfo.file_url) {
                                   const finalA = (/^https?:\/\//i.test(String(adminSignInfo.file_url))) ? adminSignInfo.file_url : resolveFileUrl(adminSignInfo.file_url, baseUrl, process.env.REACT_APP_FILE_URL || '');
                                   exportPrefetchedRawSigUrls[1] = finalA;
                                   exportPrefetchedSigDataUrls[1] = await loadImageAsDataUrl(finalA);
                              }
                         } catch (e) {
                              // ignore data-url conversion errors and fall back to previously fetched values
                         }

                         // Pass prefetched signature data to the PDF exporter so it doesn't re-fetch
                         const evalForPdf = { ...form, sign: signInfo || null, admin_sign: adminSignInfo || null, admin_name: adminName || form.admin_name || null, admin_emp_code: adminEmp || form.admin_emp_code || null };
                         await exportEvaluationToPDF({
                              evaluation: evalForPdf,
                              details: Object.values(details || {}),
                              id,
                              baseUrl,
                              publicUrl: process.env.PUBLIC_URL || '',
                              envFileUrl: process.env.REACT_APP_FILE_URL || '',
                              outputMode: 'open',
                              prefetchedSigDataUrls: exportPrefetchedSigDataUrls,
                              prefetchedRawSigUrls: exportPrefetchedRawSigUrls,
                         });
                    } catch (pdfErr) {
                         console.debug('PDF export failed after save', pdfErr);
                    }
                    // After a final submit, redirect to the admin evaluations list.
                    if (finalSubmit) navigate('/admin-evaluations');
                    else navigate(`/admin-evaluations/${id}`);

               } else {
                    setMessage({ type: 'error', text: json.error || JSON.stringify(json) });
               }
          } catch (err) {
               console.error(err);
               setMessage({ type: 'error', text: 'Request failed' });
          } finally {
               setSaving(false);
          }
     }



     if (loading) return <div className="text-center py-4"><PageLoder /></div>;

     return (
          <div className="consultant-eval compact" data-instance-id={instanceId.current}>


               {/* Toast message */}
               {message && (
                    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
                         <div style={{ padding: '10px 14px', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', background: message.type === 'error' ? '#f8d7da' : '#d4edda', color: message.type === 'error' ? '#721c24' : '#155724', border: message.type === 'error' ? '1px solid #f5c6cb' : '1px solid #c3e6cb' }}>
                              {message.text}
                         </div>
                    </div>
               )}

               <form onSubmit={(e) => handleSubmit(e, true)}>
                    <div className="form-grid">

                         <div style={{ gridColumn: '1 / -1', marginBottom: 5 }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, }}>
                                   <div> <strong> <span style={{ color: 'red' }}> Consultant Evaluation</span></strong></div>
                                   <div><strong>Emp Code:</strong> {form.emp_code || ''}</div>
                                   <div><strong>Name:</strong> {form.emp_name || ''}</div>
                                   <div><strong>Centre:</strong> {form.centre || ''}</div>
                                   <div><strong>Specialty:</strong> {form.specialty || ''}</div>
                                   <div><strong>Timing:</strong> {form.timing || ''}</div>
                                   <div><strong>Mobile:</strong> {form.mobile || ''}</div>
                                   <div><strong>Employment Type:</strong> {form.employment_type || ''}</div>
                                   <div><strong>DOJ:</strong> {(form.doj && form.doj !== '0000-00-00') ? formatDateOnly(form.doj) : ''}</div>
                                   <div><strong>VDC Exp:</strong> {form.vdc_exp || ''}</div>
                                   <div><strong>Department:</strong> {form.dept || ''}</div>

                                   <div><strong>Submitted:</strong> {form.is_submitted === 'Y' ? `Yes${form.submitted_on ? ', on ' + formatDateTime(form.submitted_on) : ''}` : 'No'}</div>
                                   <div><strong>Created on:</strong> {formatDateTime(form.created_on) || ''}</div>
                                   <div><strong>Updated on:</strong> {formatDateTime(form.updated_on) || ''}</div>
                                   <div><strong>Total  Points:</strong> {(form.total_admin_points !== undefined && form.total_admin_points !== '') ? form.total_admin_points : Object.values(details || {}).reduce((s, it) => s + (parseInt(it.admin_points, 10) || 0), 0)}</div>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 10 }}>
                                   <div><strong> Quarter:</strong> {form.quarter}</div>
                                   <div><strong> Year:</strong> {form.year}</div>
                                   <div><strong>Is Submitted: </strong> {form.is_submitted === 'Y' ? 'Yes' : 'No'}</div>
                              </div>
                         </div>
                         <input type='hidden' name="quarter" value={form.quarter} />
                         <input type='hidden' name="year" value={form.year} />

                    </div>


                    <div className="question-list">
                         {questions.length === 0 && <div>No questions available.</div>}
                         <table className="admin-eval-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                   <tr>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eaeaea' }}>Sr. No</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eaeaea' }}>Task</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eaeaea' }}>Consultant Points</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eaeaea' }}>Consultant Comments</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eaeaea' }}> Points</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eaeaea' }}> Comments</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {questions.map((q, idx) => (
                                        <tr key={q.id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                                             <td style={{ padding: '8px', verticalAlign: 'top' }}>{q.display_order || idx + 1}</td>
                                             <td style={{ padding: '8px', verticalAlign: 'top', width: '35%', wordBreak: 'break-word' }}>{q.question_text}</td>
                                             <td style={{ padding: '8px', verticalAlign: 'top', width: '10%', minWidth: '140px' }}>
                                                  <div className="readonly-field" style={{ display: 'inline-block', padding: '4px 10px', border: '1px solid #e8e8e8', borderRadius: 4, background: getBgColor(details[q.id]?.points), color: getTextColor(details[q.id]?.points) }}>
                                                       {(() => {
                                                            const v = details[q.id]?.points;
                                                            switch (String(v)) {
                                                                 case '3': return 'Good';
                                                                 case '2': return 'Satisfactory';
                                                                 case '1': return 'Non Satisfactory';
                                                                 case '0': return 'Nothing is done';
                                                                 case '':
                                                                 case undefined:
                                                                      return '-';
                                                                 default:
                                                                      return v;
                                                            }
                                                       })()}
                                                  </div>
                                             </td>
                                             <td style={{ padding: '8px', width: '15%', verticalAlign: 'top' }}>
                                                  <div style={{ maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{details[q.id]?.comments || '-'}</div>
                                             </td>
                                             <td style={{ padding: '8px', width: '10%', verticalAlign: 'top' }}>
                                                  <select
                                                       value={(details[q.id]?.admin_points === '' || details[q.id]?.admin_points == null) ? '' : details[q.id].admin_points.toString()}
                                                       onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val === '') handleDetailChange(q.id, 'admin_points', '');
                                                            else handleDetailChange(q.id, 'admin_points', parseInt(val, 10));
                                                       }}
                                                       className="points-input"
                                                       style={{ width: '100%', backgroundColor: getBgColor(details[q.id]?.admin_points), color: getTextColor(details[q.id]?.admin_points) }}
                                                       disabled={!(isAdminUser && form.is_submitted === 'Y')}
                                                       required
                                                  >
                                                       <option value="">Select</option>
                                                       <option value="3">Good</option>
                                                       <option value="2">Satisfactory</option>
                                                       <option value="1">Non Satisfactory</option>
                                                       <option value="0">Nothing is done</option>
                                                  </select>
                                             </td>
                                             <td style={{ padding: '8px', width: '40%', verticalAlign: 'top' }}>
                                                  <input
                                                       value={details[q.id]?.admin_comments ?? ''}
                                                       onChange={(e) => handleDetailChange(q.id, 'admin_comments', e.target.value)}
                                                       placeholder=" Comments"
                                                       className="comments-input"
                                                       style={{ width: '100%', backgroundColor: getBgColor(details[q.id]?.admin_points), borderColor: detailErrors[q.id] ? '#c74343' : undefined }}
                                                       disabled={!(isAdminUser && form.is_submitted === 'Y')}

                                                       // make comments required when admin_points is 1 or 0
                                                       required={isAdminUser && form.is_submitted === 'Y' && (String(details[q.id]?.admin_points) === '1' || String(details[q.id]?.admin_points) === '0')}
                                                       aria-invalid={detailErrors[q.id] ? 'true' : 'false'}
                                                  />
                                                  {(
                                                       isAdminUser && form.is_submitted === 'Y' &&
                                                       (String(details[q.id]?.admin_points) === '1' || String(details[q.id]?.admin_points) === '0') &&
                                                       (!details[q.id]?.admin_comments || String(details[q.id].admin_comments).trim() === '')
                                                  ) && (
                                                            <div style={{ color: '#c74343', fontSize: 12, marginTop: 6 }}>This comment is required for the selected rating.</div>
                                                       )}
                                             </td>
                                        </tr>
                                   ))}
                              </tbody>
                              <tfoot>
                                   <tr>
                                        <td colSpan={2} style={{ padding: '8px', fontWeight: 700, textAlign: 'right' }}>Total Points (45) </td>
                                        <td style={{ padding: '8px' }}>
                                             <input name="total_points"
                                                  value={Object.values(details).reduce((s, it) => {
                                                       const v = parseInt(it.points, 10);
                                                       return s + (Number.isNaN(v) ? 0 : v);
                                                  }, 0)} readOnly className="points-input" />
                                        </td>
                                        <td></td>
                                        <td style={{ padding: '8px' }}>
                                             <input name="total_admin_points" style={{ backgroundColor: '#e6f7ff', width: '100%' }}
                                                  value={Object.values(details).reduce((s, it) => {
                                                       const v = parseInt(it.admin_points, 10);
                                                       return s + (Number.isNaN(v) ? 0 : v);
                                                  }, 0)} readOnly className="points-input" />
                                        </td>
                                        <td></td>
                                   </tr>
                              </tfoot>
                         </table>
                    </div>

                    {isAdminUser && form.is_submitted === 'Y' ? (
                         <>
                              <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                   <label style={{ display: 'block', marginBottom: 0, minWidth: 140 }}>
                                        <strong>Overall Remarks</strong>
                                   </label>
                                   <textarea
                                        name="overall_comment"
                                        value={form.overall_comment ?? ''}
                                        onChange={(e) => setForm(f => ({ ...f, overall_comment: e.target.value }))}
                                        placeholder="Add overall remarks here"
                                        style={{ flex: '1 1 auto', minHeight: 40, maxHeight: 120, width: '100%', maxWidth: 300 }}
                                   />
                              </div>

                              <div className="form-actions">
                                   <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Submitting…' : 'Submit  Evaluation'}</button>
                                   <button type="button" className="btn" style={{ marginLeft: 8 }} onClick={() => navigate(-1)}>Cancel</button>
                              </div>
                         </>
                    ) : (
                         <div className="form-submitted" style={{ padding: '12px 0', color: form.is_submitted === 'Y' ? '#0a66ff' : '#c66', fontWeight: 600 }}>
                              {form.is_submitted === 'Y' ? 'You do not have permission to edit this evaluation.' : 'Consultant has not submitted this evaluation yet.  cannot edit.'}
                         </div>
                    )}
               </form>
          </div>
     );
}
